import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { property } from 'lit/decorators.js';
import { style } from './zui-shell-css.js';
import { ZuiShellBase } from './zui-shell-base';
import type { PropertyValues } from 'lit';

/**
 * The Shell wraps around all of our applications and provides suite level consistencies, branding, and common functionality. This element, in particular, wraps all `<zui-shell>` custom sub-elements.
 *
 * @element zui-shell
 *
 * @slot - Default, unnamed slot; for inserting `<zui-shell-topbar>`, `<zui-shell-nav>`, `<zui-shell-content>`, and `<zui-shell-footer>` elements into `<zui-shell>` only
 *
 * @cssprop [--zui-background=var(--zui-gray-50)] - Background color of your application `<body>`
 * @cssprop [--zui-shell-background=var(--zui-background)] - Background color of `<zui-shell>` and `<zui-shell-content>`
 */
export class ZuiShell extends ZuiShellBase {
  /**
   * (optional): The user-friendly name for this application; only applicable to anchor solutions and Client Cloud
   */
  @property({ type: String, attribute: 'app-name' })
  appName = '';
  private _useSlottedAppName = false;

  static get styles() {
    return [super.styles, style];
  }

  #shellElementsObserver: MutationObserver;
  #boundShellMutationObserver = this.#shellMutationObserver.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this._storeElement(this);

    this._appName = this.appName;

    const slottedAppElement = this.querySelector('[slot="app"]');
    if (!this.appName && slottedAppElement) {
      this._useSlottedAppName = true;
      this._appName = slottedAppElement.textContent;
    }

    this.#shellElementsObserver = new MutationObserver(this.#boundShellMutationObserver);
    this.#shellElementsObserver?.observe(this, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._removeElement(this);
    this.#shellElementsObserver?.takeRecords();
    this.#shellElementsObserver?.disconnect();
  }

  firstUpdated() {
    this.#assignSlots();
  }

  updated(changedProps: PropertyValues) {
    if (changedProps.has('appName') && !this._useSlottedAppName) {
      this._appName = this.appName;
    }
  }

  render() {
    return html`<section class="shell-wrap" id="shell-wrap">
      <slot name="banner"></slot>
      <slot name="topbar"></slot>
      <slot name="nav"></slot>
      <div
        id="shell-content-wrap"
        class="shell-content-wrap ${classMap({
          'no-appbar': !this._hasAppBar,
          'no-app-name': !this._appName,
        })}"
      >
        <slot name="content"></slot>
        <slot name="footer"></slot>
      </div>
    </section>`;
  }

  #assignSlots() {
    this.querySelector('zui-shell-banner:not([slot])')?.setAttribute('slot', 'banner');
    this.querySelector('zui-shell-topbar:not([slot])')?.setAttribute('slot', 'topbar');
    this.querySelector('zui-shell-nav:not([slot])')?.setAttribute('slot', 'nav');
    this.querySelector('zui-shell-content:not([slot])')?.setAttribute('slot', 'content');
    this.querySelector('zui-shell-footer:not([slot])')?.setAttribute('slot', 'footer');
  }

  #shellMutationObserver(mutations: MutationRecord[]) {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        this.#assignSlots();
      }
    }
  }
}

window.customElements.define('zui-shell', ZuiShell);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell': ZuiShell;
  }
}
